import raw from './fundingStages.json';

/**
 * fundingStages.ts — the SINGLE source of truth for the funding-stages mapping
 * (D-058 / RULES_BLOG_POST Rule 42). The blog figure (FundingStagesExplorer), the
 * entity wizard raise-profile step and (via the entity record) the investor
 * funnel all read from here — one data file, one formatter, kept in sync.
 *
 * All numbers are INDICATIVE ranges (structural model defaults for the raise-profile
 * prefill), not live fundraising data; they vary by region and industry.
 */

export type Investor = { type: string; ticketMin: number; ticketMax: number; open?: boolean };
export type Stage = {
  order: number;
  round: string;
  wizardStages: string[];
  raiseMin: number;
  raiseMax: number;
  raiseOpen?: boolean;
  investors: Investor[];
  maturityGate: string;
  whatThisMeans?: string;
};

export const STAGES: Stage[] = (raw as { stages: Stage[] }).stages;

// ── ONE shared formatter (Rule 21): labels, tooltip, sliders and table can never drift ──

export const money = (n: number): string => {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `$${Number.isInteger(m) ? m : m.toFixed(1)}M`;
  }
  const k = n / 1_000;
  return `$${Number.isInteger(k) ? k : Math.round(k)}k`;
};

export const range = (min: number, max: number, open?: boolean) =>
  `${money(min)}–${money(max)}${open ? '+' : ''}`;

export const stageTicket = (s: Stage): string => {
  const min = Math.min(...s.investors.map((i) => i.ticketMin));
  const maxInv = s.investors.reduce((a, b) => (b.ticketMax > a.ticketMax ? b : a), s.investors[0]);
  return range(min, maxInv.ticketMax, maxInv.open);
};

export const wizardLabel = (s: Stage) => s.wizardStages.map((w) => w.replace('_', ' ')).join(' / ');

// ── Mapping helpers ────────────────────────────────────────────────────────────────

/** The stage whose wizardStages includes this wizard answer (idea/mvp/…/scale). */
export const fundingForWizardStage = (wizardStage: string): Stage | null =>
  STAGES.find((s) => s.wizardStages.includes(wizardStage)) ?? null;

/** Round name for a wizard stage, e.g. "idea" → "Pre-seed". */
export const roundForWizardStage = (wizardStage: string): string =>
  fundingForWizardStage(wizardStage)?.round ?? '';

/** The overall ticket band for a stage: min across all investor ticketMins, max across ticketMaxs. */
export const ticketBounds = (s: Stage): { min: number; max: number } => {
  const min = Math.min(...s.investors.map((i) => i.ticketMin));
  const max = s.investors.reduce((a, b) => (b.ticketMax > a.ticketMax ? b : a), s.investors[0]).ticketMax;
  return { min, max };
};

// ── Selection defaults (the user picks a stage → these are prefilled; user adjusts) ──

/** "Default it into the avg between min and max" — midpoint of a range. */
export const midpoint = (min: number, max: number): number => Math.round((min + max) / 2);

/** The "middle" of a range on a LOG scale (geometric mean). Used so the default ball sits
 * CENTRED in the shaded typical band — the arithmetic mean sits off-centre on a log track. */
export const geoMean = (min: number, max: number): number => Math.round(Math.sqrt(min * max));

/** Snap a value to the nearest $5k step — the user wants clean, even numbers only. */
export const snapStep = (v: number, step = 5000): number => Math.round(v / step) * step;

export const raiseMidpoint = (s: Stage): number => midpoint(s.raiseMin, s.raiseMax);

export const ticketMidpoint = (s: Stage): number => {
  const { min, max } = ticketBounds(s);
  return midpoint(min, max);
};

/** A sensible slider step (~0.5% of the range, floored at $1k) so dragging is not noisy. */
export const stepForRange = (min: number, max: number): number =>
  Math.max(1000, Math.round((max - min) / 200));

/** Parse a money string the user types ("$2M", "1.5M", "500k", "1,500,000") to a number.
 * Returns null when unparseable. Full control — any amount is accepted, not just the range. */
export const parseMoney = (s: string): number | null => {
  const t = (s ?? '').trim().replace(/,/g, '');
  if (!t) return null;
  const m = t.match(/^[$€£]?\s*([\d.]+)\s*([kmb]?)$/i);
  if (!m) return null;
  const num = parseFloat(m[1]);
  if (!isFinite(num)) return null;
  const mult = { k: 1e3, m: 1e6, b: 1e9 }[(m[2] || '').toLowerCase()] ?? 1;
  return Math.round(num * mult);
};
